import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, LockKeyhole, PlayCircle, RotateCcw, Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { STATUS_ICON_SIZE } from "@/constants/ui";
import { api, formatApiErrorDetail } from "@/services/api";

function progressFor(progress, lessonId) {
  return progress.lessons.find((item) => item.lesson_id === lessonId) || { status: "not_started", best_score: 0, attempts: 0 };
}

function StudyModuleCard({ activeModuleId, item, onSelect }) {
  const isActive = activeModuleId === item.module.id;
  return (
    <button type="button" className={`grammar-module-card ${isActive ? "grammar-module-card-active" : ""}`} onClick={() => onSelect(item.module.id)} data-testid={`study-module-card-${item.module.id}`}>
      <span className="grammar-module-order" data-testid={`study-module-order-${item.module.id}`}>{item.module.order}</span>
      <span className="label-chip bg-white" data-testid={`study-module-level-${item.module.id}`}>{item.module.level}</span>
      <strong data-testid={`study-module-title-${item.module.id}`}>{item.module.title}</strong>
      <small data-testid={`study-module-description-${item.module.id}`}>{item.module.description_pt}</small>
      <span className="grammar-progress-track" data-testid={`study-module-track-${item.module.id}`}>
        <span style={{ width: `${item.progress.percent_complete}%`, background: item.module.accent }} data-testid={`study-module-fill-${item.module.id}`} />
      </span>
      <em data-testid={`study-module-progress-${item.module.id}`}>{item.progress.completed_lessons}/{item.progress.total_lessons} aulas · {item.progress.percent_complete}%</em>
    </button>
  );
}

function ExerciseInput({ exercise, value, onAnswer }) {
  if (exercise.type === "choice") {
    return (
      <div className="grammar-options" data-testid={`study-exercise-options-${exercise.id}`}>
        {exercise.options.map((option) => (
          <button key={option} type="button" className={`grammar-option-button ${value === option ? "grammar-option-selected" : ""}`} onClick={() => onAnswer(exercise.id, option)} data-testid={`study-exercise-option-${exercise.id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{option}</button>
        ))}
      </div>
    );
  }
  return <input className="auth-input mt-3" value={value || ""} onChange={(event) => onAnswer(exercise.id, event.target.value)} placeholder="Digite sua resposta" data-testid={`study-exercise-input-${exercise.id}`} />;
}

function ExerciseBlock({ exercise, onAnswer, result, value }) {
  return (
    <article className="grammar-exercise" data-testid={`study-exercise-${exercise.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="zero-label" data-testid={`study-exercise-prompt-${exercise.id}`}>{exercise.prompt_pt}</p>
          <h3 className="mt-2 font-display text-2xl font-black" data-testid={`study-exercise-question-${exercise.id}`}>{exercise.question}</h3>
        </div>
        {result && <span className={`grammar-result-pill ${result.correct ? "grammar-result-correct" : "grammar-result-wrong"}`} data-testid={`study-exercise-result-${exercise.id}`}>{result.correct ? "certo" : "rever"}</span>}
      </div>
      <ExerciseInput exercise={exercise} value={value} onAnswer={onAnswer} />
      {result && <p className="mt-3 text-sm font-black text-slate-600" data-testid={`study-exercise-explanation-${exercise.id}`}>Resposta: {result.expected_answer}. {result.explanation_pt}</p>}
    </article>
  );
}

function StudyLessonBadges({ lessonId, progress }) {
  if (progress.status === "completed") return <span className="profile-badge" data-testid={`study-lesson-completed-${lessonId}`}><CheckCircle2 size={18} /> concluída</span>;
  if (progress.locked) return <span className="profile-badge" data-testid={`study-lesson-locked-${lessonId}`}><LockKeyhole size={18} /> bloqueada</span>;
  if (progress.recommended) return <span className="profile-badge" data-testid={`study-lesson-recommended-${lessonId}`}>aula atual</span>;
  return null;
}

function StudyLessonActions({ allAnswered, done, lesson, locked, navigate, onReview, onSubmit, pending, progress }) {
  return (
    <div className="mt-5 flex flex-wrap gap-3" data-testid={`study-lesson-actions-${lesson.id}`}>
      {!locked && !done && <button type="button" className="primary-action w-auto" onClick={() => onSubmit(lesson.id)} disabled={pending || !allAnswered} data-testid={`study-lesson-submit-button-${lesson.id}`}>{progress.review_required ? "Tentar após revisão" : "Corrigir exercícios"}</button>}
      {progress.review_required && <button type="button" className="secondary-action w-auto" onClick={() => onReview(lesson.id)} data-testid={`study-lesson-review-button-${lesson.id}`}><RotateCcw size={18} /> Revisei, liberar tentativa</button>}
      {lesson.linked_scenario_id && <button type="button" className="secondary-action w-auto" onClick={() => navigate(`/lesson/${lesson.linked_scenario_id}`)} data-testid={`study-lesson-scenario-button-${lesson.id}`}>Praticar cenário</button>}
    </div>
  );
}

function StudyLessonExercises({ lesson, lessonState, locked, onAnswer, resultById }) {
  if (locked) return null;
  return (
    <div className="mt-5 grid gap-4" data-testid={`study-lesson-exercise-list-${lesson.id}`}>
      {lesson.exercises.map((exercise) => <ExerciseBlock key={exercise.id} exercise={exercise} onAnswer={(id, value) => onAnswer(lesson.id, id, value)} result={resultById[exercise.id]} value={lessonState.answers[exercise.id]} />)}
    </div>
  );
}

function StudyLessonCard({ activeModule, lesson, lessonState, navigate, onAnswer, onReview, onSubmit, pending }) {
  const progress = progressFor(activeModule.progress, lesson.id);
  const done = progress.status === "completed";
  const locked = progress.locked;
  const resultById = Object.fromEntries((lessonState.attemptResult?.results || []).map((item) => [item.exercise_id, item]));
  const allAnswered = lesson.exercises.every((exercise) => (lessonState.answers[exercise.id] || "").trim());
  const exerciseListTestId = `study-lesson-exercise-list-${lesson.id}`;
  const scrollToExercises = () => document.querySelector(`[data-testid="${exerciseListTestId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  return (
    <article className={`neo-card surface-neutral p-5 ${locked ? "opacity-60" : ""}`} data-testid={`study-lesson-card-${lesson.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid={`study-lesson-category-${lesson.id}`}>{activeModule.module.category} · {lesson.skill} · aula {lesson.difficulty}/7</p>
          <h2 className="mt-3 font-display text-3xl font-black" data-testid={`study-lesson-title-${lesson.id}`}>{lesson.title}</h2>
        </div>
        <StudyLessonBadges lessonId={lesson.id} progress={progress} />
      </div>
      <p className="mt-4 text-base font-bold text-slate-700" data-testid={`study-lesson-objective-${lesson.id}`}>{lesson.objective_pt}</p>
      {!locked && !done && <div className="next-step-box" data-testid={`study-next-step-${lesson.id}`}>
        <span>Próximo passo: responda os exercícios abaixo e clique em “Corrigir exercícios”.</span>
        <button type="button" className="next-step-jump-button" onClick={scrollToExercises} data-testid={`study-jump-to-exercises-button-${lesson.id}`}>Ver exercícios desta aula</button>
      </div>}
      <ol className="mt-4 grid gap-2 pl-5 text-sm font-black text-slate-600" data-testid={`study-lesson-steps-${lesson.id}`}>{lesson.steps.map((step) => <li key={step}>{step}</li>)}</ol>
      <div className="grammar-score-panel mt-4" data-testid={`study-lesson-practice-${lesson.id}`}><PlayCircle size={STATUS_ICON_SIZE} /><span>{lesson.practice_prompt}</span></div>
      <StudyLessonExercises lesson={lesson} lessonState={lessonState} locked={locked} onAnswer={onAnswer} resultById={resultById} />
      {lessonState.attemptResult && <div className="grammar-score-panel mt-4" data-testid={`study-attempt-result-${lesson.id}`}><Sparkles size={STATUS_ICON_SIZE} /><span>{lessonState.attemptResult.passed ? "Aula concluída. Próxima aula liberada." : "Revise as respostas antes de tentar novamente."}</span></div>}
      {lessonState.error && <div className="auth-error mt-4" role="alert" data-testid={`study-attempt-error-${lesson.id}`}>{lessonState.error}</div>}
      <StudyLessonActions allAnswered={allAnswered} done={done} lesson={lesson} locked={locked} navigate={navigate} onReview={onReview} onSubmit={onSubmit} pending={pending} progress={progress} />
    </article>
  );
}

export default function Modules() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [activeModuleId, setActiveModuleId] = useState("");
  const [lessonState, setLessonState] = useState({});
  const { data: modules = [], isLoading } = useQuery({ queryKey: ["study-modules"], queryFn: api.getStudyModules });
  const activeModule = modules.find((item) => item.module.id === activeModuleId) || modules[0];
  const currentLesson = activeModule?.module.lessons.find((lesson) => {
    const progress = progressFor(activeModule.progress, lesson.id);
    return progress.recommended || (!progress.locked && progress.status !== "completed");
  }) || activeModule?.module.lessons[0];
  const remainingLessons = activeModule?.module.lessons.filter((lesson) => lesson.id !== currentLesson?.id) || [];

  const mutation = useMutation({
    mutationFn: ({ lessonId }) => api.submitStudyAttempt(lessonId, Object.entries(lessonState[lessonId]?.answers || {}).map(([exercise_id, answer]) => ({ exercise_id, answer })), Boolean(lessonState[lessonId]?.reviewConfirmed)),
    onSuccess: (result) => {
      setLessonState((current) => ({ ...current, [result.lesson_id]: { ...(current[result.lesson_id] || {}), attemptResult: result, error: "", reviewConfirmed: false } }));
      queryClient.invalidateQueries({ queryKey: ["study-modules"] });
      queryClient.invalidateQueries({ queryKey: ["recommendations"] });
    },
    onError: (error, variables) => setLessonState((current) => ({ ...current, [variables.lessonId]: { ...(current[variables.lessonId] || { answers: {} }), error: formatApiErrorDetail(error.response?.data?.detail) } })),
  });

  const handleAnswer = (lessonId, exerciseId, answer) => setLessonState((current) => ({ ...current, [lessonId]: { answers: { ...(current[lessonId]?.answers || {}), [exerciseId]: answer }, attemptResult: null, error: "", reviewConfirmed: current[lessonId]?.reviewConfirmed || false } }));
  const handleReview = (lessonId) => setLessonState((current) => ({ ...current, [lessonId]: { ...(current[lessonId] || { answers: {} }), reviewConfirmed: true, error: "Revisão confirmada. Agora tente novamente." } }));
  const focusCurrentExercise = Boolean(location.state?.focusCurrentExercise);
  const scrollToCurrentExercise = useCallback(() => {
    document.querySelector('[data-testid="modules-current-exercise-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  useEffect(() => {
    if (!focusCurrentExercise || isLoading) return undefined;
    const scrollTimer = window.setTimeout(scrollToCurrentExercise, 120);
    return () => window.clearTimeout(scrollTimer);
  }, [focusCurrentExercise, isLoading, scrollToCurrentExercise]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="modules-page">
      <section className="page-title-row" data-testid="modules-title-section">
        <p className="section-kicker" data-testid="modules-page-kicker">trilha interativa</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="modules-page-heading">5 módulos, 35 aulas, desbloqueio por exercício.</h1>
        <p className="max-w-3xl text-lg font-semibold text-slate-600" data-testid="modules-page-description">Escolha um módulo. A aula atual mostra os exercícios logo abaixo. Responda e clique em Corrigir.</p>
      </section>
      {isLoading ? <div className="neo-card surface-neutral mt-8 p-6" data-testid="modules-loading-state">Carregando módulos...</div> : (
        <>
          {currentLesson && <section className="easy-current-exercise" data-testid="modules-current-exercise-section">
            <p className="section-kicker" data-testid="modules-current-exercise-kicker">seu exercício agora</p>
            <StudyLessonCard activeModule={activeModule} lesson={currentLesson} lessonState={lessonState[currentLesson.id] || { answers: {}, attemptResult: null, error: "" }} navigate={navigate} onAnswer={handleAnswer} onReview={handleReview} onSubmit={(lessonId) => mutation.mutate({ lessonId })} pending={mutation.isPending} />
          </section>}
          <section className="grammar-module-grid" data-testid="study-module-grid">{modules.map((item) => <StudyModuleCard key={item.module.id} activeModuleId={activeModule?.module.id} item={item} onSelect={setActiveModuleId} />)}</section>
          <section className="mt-8 grid gap-5 lg:grid-cols-2" data-testid="study-lesson-grid">
            {remainingLessons.map((lesson) => <StudyLessonCard key={lesson.id} activeModule={activeModule} lesson={lesson} lessonState={lessonState[lesson.id] || { answers: {}, attemptResult: null, error: "" }} navigate={navigate} onAnswer={handleAnswer} onReview={handleReview} onSubmit={(lessonId) => mutation.mutate({ lessonId })} pending={mutation.isPending} />)}
          </section>
        </>
      )}
    </div>
  );
}